import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  inject,
  Injectable,
  makeStateKey,
  PLATFORM_ID,
  signal,
  TransferState,
} from '@angular/core';
import { of } from 'rxjs';
import { Seo } from '../../interfaces/seo';
import { CacheService } from '../../ssr-services/cache/cache.service';
import { SubscriberService } from '../../ssr-services/subscriber/subscriber.service';
import { SEO_CONTENT_MANAGER_TOKEN } from '../../tokens/server-tokens';

const SEO_CONTENT_STATE_KEY = makeStateKey<Seo>('SEO_CONTENT_STATE_KEY');

@Injectable({
  providedIn: 'root',
})
export class SeoContentService {
  private readonly seoManager = inject(SEO_CONTENT_MANAGER_TOKEN, { optional: true });
  private readonly subscriberService = inject(SubscriberService);
  private readonly cacheService = inject(CacheService);
  private readonly transferState = inject(TransferState);
  private readonly platform = inject(PLATFORM_ID);

  public seoContent = signal<Seo>({});

  constructor() {
    if (isPlatformBrowser(this.platform)) {
      // Get SEO content from TransferState (set by server)
      this.seoContent.set(this.transferState.get(SEO_CONTENT_STATE_KEY, {}));
    }
  }

  /**
   * Fetch SEO content on server (from pre-warmed memory cache)
   * This is FAST because content is already in memory!
   *
   * @returns Observable of SEO content
   */
  public fetchContentInServer() {
    if (!isPlatformServer(this.platform)) {
      console.warn('SeoContent: fetchContentInServer called on browser, returning empty');
      return of({} as Seo);
    }

    // If SEO manager not available, use local fallback
    if (!this.seoManager) {
      console.warn('SeoContent: Manager not available, using local fallback');
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const fallback = require('../../guards/ssr/seo-handling/seo_en.json') as Seo;
      this.transferState.set(SEO_CONTENT_STATE_KEY, fallback);
      this.seoContent.set(fallback);
      return of(fallback);
    }

    const language = this.subscriberService.subscriberLanguage().code.toLowerCase();
    const host = this.cacheService.getHost();

    // Get from memory cache (instant! ~1ms)
    const content = this.seoManager.getSeoContent(host, language);

    if (content && Object.keys(content).length > 0) {
      console.log(`✅ SEO content retrieved from memory cache for ${host}.${language}`);
      this.transferState.set(SEO_CONTENT_STATE_KEY, content as Seo);
      this.seoContent.set(content as Seo);
      return of(content as Seo);
    }

    // Fallback (should never happen if warmup worked)
    console.warn(`⚠️  SEO content not in cache for ${host}.${language}, using local fallback`);
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const fallback = require('../../guards/ssr/seo-handling/seo_en.json') as Seo;
    this.transferState.set(SEO_CONTENT_STATE_KEY, fallback);
    this.seoContent.set(fallback);
    return of(fallback);
  }
}
